import React, { useState, useEffect } from 'react';

/**
 * Enhanced Analysis Result component with AI heatmap overlay
 *  
 * This component displays the analyzed medical image with an AI-generated
 * heatmap overlay showing areas of interest highlighted by color based on
 * condition and confidence.
 */
const EnhancedAnalysisResult = ({ 
  image, 
  conditions, 
  activeCondition,
  onConditionClick,
  findings,
  confidence,
  recommendation
}) => {
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [brightnessValue, setBrightnessValue] = useState(100);
  const [contrastValue, setContrastValue] = useState(100);
  const [showExplanation, setShowExplanation] = useState(false);
  const [activeExplanation, setActiveExplanation] = useState(null);

  // Create SVG heatmap overlay
  const createHeatmapMask = () => {
    // Only show conditions with a location and probability > 20
    const displayableConditions = conditions.filter(c => c.location && c.probability > 20);
    
    if (displayableConditions.length === 0) return null;
    
    const maskSvg = `
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          ${displayableConditions.map((condition, idx) => `
            <radialGradient id="heatGradient${idx}" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
              <stop offset="0%" stop-color="${getColorForCondition(condition)}" stop-opacity="${condition.probability/100 * 0.7}" />
              <stop offset="70%" stop-color="${getColorForCondition(condition)}" stop-opacity="${condition.probability/100 * 0.4}" />
              <stop offset="100%" stop-color="${getColorForCondition(condition)}" stop-opacity="0" />
            </radialGradient>
          `).join('')}
        </defs>
        ${displayableConditions.map((condition, idx) => `
          <ellipse 
            cx="${condition.location.x}%" 
            cy="${condition.location.y}%" 
            rx="${condition.location.radius}%" 
            ry="${condition.location.radius}%" 
            fill="url(#heatGradient${idx})" 
          />
        `).join('')}
      </svg>
    `;
    
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(maskSvg)}`;
  };

  // Get appropriate color based on condition severity and type
  const getColorForCondition = (condition) => {
    if (condition.severity === 'Severe') return '#FF3366'; // Red for severe conditions
    if (condition.severity === 'Moderate') return '#FFCC00'; // Yellow for moderate
    return '#00CCFF'; // Blue for mild/none
  };

  const handleBrightnessChange = (e) => {
    setBrightnessValue(parseInt(e.target.value));
  };

  const handleContrastChange = (e) => {
    setContrastValue(parseInt(e.target.value));
  };

  // Patient-friendly explanations for various conditions
  const getConditionExplanation = (conditionName) => {
    const explanations = {
      // X-ray conditions — must match torchxrayvision's actual 19 output
      // labels exactly (xrv.datasets.default_pathologies + "No Finding");
      // the previous entries used different names ("Pleural Effusion" vs.
      // the model's "Effusion", "Pulmonary Edema" vs. "Edema", "Lung Nodule"
      // vs. "Nodule") and were missing "Infiltration", "Consolidation",
      // "Mass", "Hernia", "Fracture", "Pleural_Thickening",
      // "Enlarged Cardiomediastinum", and "Lung Lesion" entirely — so most
      // real findings silently fell through to "No detailed explanation".
      'No Finding': "No specific abnormality was detected by the AI screen. This does not rule out disease — it means none of the 18 patterns the model looks for reached its detection threshold on this image.",
      'Infiltration': "An area where fluid, cells, or other material has accumulated in the lung tissue, appearing as hazy or cloudy patches on X-ray. Can indicate infection, inflammation, or fluid buildup — a non-specific finding requiring clinical correlation.",
      'Consolidation': "A region of lung tissue that has filled with liquid instead of air, appearing as a dense, well-defined white area on X-ray. Commonly associated with pneumonia.",
      'Pneumonia': "An infection that causes inflammation in the air sacs of your lungs, which can fill with fluid. This appears as white patches or areas of cloudiness on X-rays.",
      'Effusion': "A buildup of excess fluid between the layers of tissue that line your lungs and chest cavity (pleural effusion). This appears as a white area, often at the bottom of the lung, on X-rays.",
      'Cardiomegaly': "An enlarged heart, which can be caused by various conditions including high blood pressure or heart disease. This appears as an enlarged heart shadow on X-rays.",
      'Enlarged Cardiomediastinum': "Widening of the central chest area containing the heart and major vessels. Can reflect an enlarged heart, aortic abnormality, or mediastinal mass — requires clinical follow-up to determine the cause.",
      'Atelectasis': "A partial or complete collapse of a lung or lobe of a lung. This appears as an area of increased density or whiteness on X-rays.",
      'Lung Opacity': "A general term for any area that appears whiter than normal on a chest X-ray, which could indicate inflammation, infection, or fluid.",
      'Lung Lesion': "A localized area of abnormal lung tissue visible on X-ray. A general finding that can represent a range of causes, from benign scarring to something requiring further imaging.",
      'Edema': "Excess fluid in the lungs, often due to heart problems (pulmonary edema). This appears as increased whiteness throughout the lungs on X-rays.",
      'Pneumothorax': "Air trapped between the lung and chest wall, causing the lung to collapse. This appears as a dark area without lung markings on X-rays.",
      'Nodule': "A small round or oval-shaped growth in the lung, typically under 3cm. It appears as a round white spot on X-rays and often warrants follow-up imaging to characterize.",
      'Mass': "A lung abnormality larger than a nodule (over 3cm), appearing as a dense white area on X-ray. Requires prompt clinical evaluation to determine its nature.",
      'Emphysema': "A condition that damages the air sacs in your lungs, making it difficult to breathe. X-rays may show increased darkness of the lungs and a flattened diaphragm.",
      'Fibrosis': "Scarring of lung tissue, which can be caused by various conditions. It appears as white lines or patches on X-rays.",
      'Pleural_Thickening': "Thickening of the pleura, the thin membrane lining the lungs and chest cavity, often from prior inflammation, infection, or asbestos exposure. Appears as a dense line along the chest wall on X-ray.",
      'Hernia': "Displacement of abdominal contents into the chest cavity through the diaphragm, most often a hiatal hernia. Appears as an unusual air- or fluid-filled structure in the lower chest on X-ray.",
      'Fracture': "A break in a bone visible on the X-ray, such as a rib fracture. Appears as a discontinuity or step-off in the normal bone contour.",
      
      'Normal Ultrasound': "No abnormal findings. Organs and structures appear normal in size, shape, and echogenicity.",
  
      // Obstetric & Fetal
      'Pregnancy Confirmation': "Gestational sac or fetal pole detected, confirming intrauterine pregnancy.",
      'Fetal Viability': "Fetal cardiac activity visualized, indicating a viable pregnancy.",
      'Ectopic Pregnancy': "Absence of intrauterine pregnancy with adnexal mass or free fluid — suspicious for ectopic pregnancy.",
      'Placenta Previa': "Placenta partially or fully covering the cervix, seen as a low-lying echogenic structure.",
      'Oligohydramnios': "Decreased amniotic fluid volume visualized, which may indicate fetal distress.",
      'Fetal Growth Restriction': "Biometric measurements show fetus is small for gestational age.",
      
      // Abdominal / Hepato-biliary
      'Gallstones': "Echogenic foci with acoustic shadowing within the gallbladder, consistent with gallstones.",
      'Cholecystitis': "Gallbladder wall thickening and pericholecystic fluid, indicating inflammation.",
      'Liver Cirrhosis': "Nodular liver contour, coarse echotexture, and signs of portal hypertension.",
      'Ascites': "Anechoic fluid seen in the peritoneal cavity, suggestive of ascites.",
      'Kidney Stones': "Bright echogenic spots with posterior shadowing in renal pelvis or calyces.",
      'Hydronephrosis': "Dilated renal collecting system appearing as anechoic fluid within the kidney.",
      
      // Cardiopulmonary
      'Pericardial Effusion': "Anechoic or hypoechoic fluid around the heart, within the pericardial sac.",
      'Cardiac Tamponade': "Large pericardial effusion with diastolic collapse of right atrium or ventricle.",
      'Pleural Effusion': "Anechoic or hypoechoic fluid collection between lung and chest wall.",
      'Pneumothorax': "Absence of lung sliding or ‘lung point’ sign indicating collapsed lung.",
      'Heart Failure': "B-lines on lung ultrasound and reduced ejection fraction on cardiac views.",
      
      // Neonatal Cranial
      'Germinal Matrix Hemorrhage': "Hyperechoic area near lateral ventricles in neonates, indicating intraventricular bleeding.",
      'Hydrocephalus (Neonatal)': "Enlarged ventricles visualized through anterior fontanelle, suggestive of CSF buildup.",
      'Periventricular Leukomalacia': "Increased echogenicity around the ventricles, often seen in premature infants.",
      
      // Soft Tissue & Trauma
      'Abscess': "Hypoechoic or anechoic fluid collection with irregular margins, possibly with internal debris.",
      'Cellulitis': "Diffuse increased echogenicity and thickening of soft tissues, often with 'cobblestoning' appearance.",
      'Foreign Body': "Hyperechoic linear object with shadowing in soft tissue, suggestive of a foreign object.",
      'Fracture (Long Bone)': "Disruption or step-off in cortical bone continuity, visible on ultrasound.",
      
      // Pelvic
      'Ovarian Cyst': "Anechoic round structure within the ovary, usually benign.",
      'Uterine Fibroid': "Hypoechoic or heterogeneous mass within uterine wall, may distort uterine contour.",
      
      // Prostate & Bladder
      'Bladder Retention': "Overdistended bladder with post-void residual volume greater than expected.",
      'Prostatomegaly': "Enlarged prostate with prominent median lobe visualized transabdominally or transrectally.",
    
      // Skin — cancer-subtype screen (HAM10000). Display names must match
      // ai_models.SKIN_LABEL_META exactly ("Benign Nevus" and "Seborrheic
      // Keratosis" below were never real output labels — the model actually
      // returns "Melanocytic Nevus (mole)" and "Benign Keratosis").
      'Melanoma': "A serious form of skin cancer that begins in the cells that produce melanin (the pigment that gives skin its color). It often appears as an irregular, dark-colored spot that changes in size, shape, or color.",
      'Basal Cell Carcinoma': "The most common type of skin cancer, which rarely spreads to other parts of the body but can cause local damage. It often appears as a pearly or waxy bump, a flat, flesh-colored lesion, or a bleeding or scabbing sore that doesn't heal.",
      'Actinic Keratosis': "A rough, scaly patch on the skin caused by years of sun exposure. It's considered a pre-cancerous growth that may develop into skin cancer if left untreated.",
      'Benign Keratosis': "A common, non-cancerous skin growth, often appearing in older adults as a waxy, 'stuck-on' patch that can be brown, black, or tan.",
      'Melanocytic Nevus (mole)': "A common, non-cancerous mole. These are typically round or oval, with smooth edges and consistent color (often brown, tan, or flesh-colored).",
      'Vascular Lesion': "An abnormal cluster of blood vessels that can appear as a red or purple mark on the skin. Common types include hemangiomas and port-wine stains.",
      'Dermatofibroma': "A common, non-cancerous skin growth that often forms after minor injury to the skin. It appears as a small, firm bump that's usually pink, gray, red, or brown.",

      // Skin — general dermatology condition screen (SCIN-derived, 44
      // classes). Display names must match ai_models.SKIN_SCIN_LABEL_META.
      'Cellulitis': "A bacterial skin infection causing redness, warmth, swelling, and tenderness that can spread quickly — needs prompt medical attention.",
      'Abscess': "A painful, swollen, pus-filled pocket under the skin caused by infection, usually requiring drainage and antibiotics.",
      'Drug Rash': "A skin reaction to a medication, ranging from mild redness to widespread blistering. Identifying and stopping the causative drug is important.",
      'Leukocytoclastic Vasculitis': "Inflammation of small blood vessels in the skin, appearing as small purple-red spots (purpura). Can signal an underlying autoimmune or systemic condition.",
      'Impetigo': "A common, contagious bacterial skin infection, typically in children, causing red sores that rupture and form a honey-colored crust.",
      'Herpes Zoster (Shingles)': "A painful, blistering rash caused by reactivation of the chickenpox virus, usually in a band on one side of the body.",
      'Scabies': "An intensely itchy skin condition caused by microscopic mites burrowing into the skin. Highly contagious through close contact.",
      'Urticaria (Hives)': "Raised, itchy welts on the skin, usually from an allergic reaction. Severe or spreading cases can signal a more serious allergic response.",
      'Purpura': "Purple-colored spots on the skin caused by bleeding underneath, which can indicate a clotting or blood vessel problem.",
      'Pigmented Purpuric Eruption': "A group of skin conditions causing reddish-brown patches from leaking small blood vessels, generally not serious.",
      'Viral Exanthem': "A widespread skin rash caused by a viral infection, often accompanied by fever and other systemic symptoms.",
      'Stasis Dermatitis': "Skin inflammation and discoloration on the lower legs from poor blood circulation, common with chronic venous disease.",
      'Photodermatitis': "A skin reaction triggered or worsened by sun exposure, causing redness, itching, or blistering in sun-exposed areas.",
      'Hypersensitivity Reaction': "A skin reaction from an overactive immune response to a trigger such as a medication, food, or environmental substance.",
      'Prurigo Nodularis': "A chronic condition with firm, intensely itchy bumps that form from repeated scratching, often linked to underlying itch conditions.",
      'Eczema': "A common condition causing dry, itchy, inflamed skin. Often chronic, with periods of flare-ups and remission.",
      'Allergic Contact Dermatitis': "A skin reaction from direct contact with an allergen (e.g. jewelry, cosmetics, plants), causing redness, itching, and sometimes blistering.",
      'Irritant Contact Dermatitis': "Skin irritation from direct contact with a harsh substance (e.g. soap, chemicals), distinct from an allergic reaction.",
      'Contact Dermatitis': "Skin inflammation from direct contact with an irritating or allergy-triggering substance, causing redness, itching, or blistering.",
      'Acute Dermatitis': "A sudden-onset episode of skin inflammation, redness, and irritation from various possible triggers.",
      'Acute/Chronic Dermatitis': "Skin inflammation that shows features of both a recent flare-up and longer-standing changes such as thickening or scaling.",
      'Chronic Dermatitis': "Long-standing skin inflammation, often with thickened, scaly, or darkened skin from repeated irritation or scratching.",
      'Psoriasis': "A chronic skin condition that causes cells to build up rapidly on the skin's surface, forming red, scaly patches that can be itchy and painful.",
      'Lichen Planus / Lichenoid Eruption': "An inflammatory condition causing itchy, flat-topped purplish bumps on the skin, and sometimes the mouth or nails.",
      'Lichen Simplex Chronicus': "Thickened, leathery skin patches that develop from a chronic itch-scratch cycle.",
      'Pityriasis Rosea': "A self-limiting rash, often starting with a single 'herald patch' followed by smaller oval patches, that typically clears on its own over weeks.",
      'Tinea (Ringworm)': "A fungal skin infection causing a ring-shaped, scaly, sometimes itchy rash. Treatable with antifungal medication.",
      'Tinea Versicolor': "A common fungal skin condition causing small patches of discolored skin, more noticeable after sun exposure.",
      'Folliculitis': "Inflammation or infection of hair follicles, appearing as small, red, sometimes pus-filled bumps.",
      'Intertrigo': "Skin irritation and inflammation in body folds (e.g. underarms, groin) from moisture, friction, and often secondary infection.",
      'Perioral Dermatitis': "A facial rash of small red bumps around the mouth, often related to certain skincare products or topical steroid use.",
      'Herpes Simplex': "A viral infection causing painful, fluid-filled blisters, commonly on the lips/mouth or genitals, that can recur.",
      'Insect Bite': "A localized skin reaction — redness, swelling, itching — from an insect bite or sting.",
      'Acne': "A common condition where hair follicles become clogged with oil and dead skin, causing pimples, blackheads, or cysts.",
      'Rosacea': "A chronic skin condition that causes redness, visible blood vessels, and sometimes small, red, pus-filled bumps on the face.",
      'Molluscum Contagiosum': "A viral skin infection causing small, firm, dome-shaped bumps, common in children and spread through direct contact.",
      'Verruca Vulgaris (Wart)': "A benign skin growth caused by HPV infection, appearing as a rough, raised bump, often on hands or feet.",
      'Granuloma Annulare': "A benign condition causing ring-shaped patches of small bumps on the skin, of unclear exact cause.",
      'Keratosis Pilaris': "A common, harmless condition causing small, rough bumps, often on the upper arms or thighs, from keratin plugging hair follicles.",
      'Scar': "Fibrous tissue that replaces normal skin after an injury, typically differing in texture or color from surrounding skin.",
      'Abrasion / Scrape / Scab': "A superficial skin injury from friction or scraping, with a scab forming as part of normal healing.",
      'Ecchymoses (Bruising)': "Discoloration of the skin from bleeding underneath, commonly known as a bruise, usually from minor trauma.",
      'Other Dermatological Finding': "A skin finding that doesn't clearly match the platform's more specific condition categories — clinician review recommended to characterize it further.",

      // Breast ultrasound (BUSI). Must match ai_models.BREAST_LABEL_META.
      'Benign mass': "A non-cancerous breast lump or mass. Benign masses are common and usually don't require treatment, though follow-up may be advised.",
      'Malignant mass': "A mass with features suspicious for breast cancer on ultrasound. Requires prompt clinical follow-up, typically including biopsy for confirmation.",
      'Normal tissue': "No mass or abnormal structure detected — breast tissue appears within normal limits on this scan.",

      // Retinal fundus — multi-disease screen (ODIR-5K-derived). Must match
      // ai_models.RETINA_LABEL_META.
      'Normal': "No signs of the screened retinal conditions were detected in this fundus image.",
      'Diabetic Retinopathy': "Damage to the blood vessels of the retina caused by diabetes, which can range from mild to vision-threatening if untreated.",
      'Glaucoma': "A group of eye conditions that damage the optic nerve, often related to elevated eye pressure, that can lead to vision loss if untreated.",
      'Age-Related Macular Degeneration': "A condition affecting the macula (central retina) that causes progressive loss of central vision, common in older adults.",
      'Hypertensive Retinopathy': "Damage to retinal blood vessels caused by high blood pressure, visible as changes in the blood vessels on fundus examination.",
      'Cataract': "Clouding of the eye's natural lens, causing blurry vision — a common, treatable age-related change.",
      'Myopia (Refractive)': "Nearsightedness — a refractive error where distant objects appear blurry, sometimes visible as retinal changes on fundus imaging in higher degrees.",
      'Other Abnormality': "A finding on the fundus image that doesn't clearly match the platform's more specific screened conditions — clinician review recommended.",

      // Diabetic foot ulcer (Gemini Vision-classified). Must match
      // ai_models.FOOT_LABEL_META.
      'Normal Foot': "No signs of ulceration or significant foot pathology detected in this image.",
      'Diabetic Foot Ulcer': "An open sore or wound, common in people with diabetes due to nerve damage and poor circulation, requiring prompt wound care to prevent complications.",
      'Infected Ulcer': "A foot ulcer showing signs of infection (e.g. redness, swelling, discharge), which needs urgent medical attention to prevent it spreading.",
      'Neuropathic Ulcer': "An ulcer caused primarily by loss of sensation (neuropathy), often on pressure points of the foot, that can go unnoticed without regular checks.",
      'Ischemic/Vascular Ulcer': "An ulcer caused by poor blood flow to the foot, often slow-healing and requiring vascular assessment.",
      'Callus / Pre-Ulcer': "Thickened skin from repeated pressure or friction — an early warning sign that, left unmanaged, can progress to an ulcer.",
      'Healed / Scarring': "Tissue that has healed from a previous wound or ulcer, shown as scarring rather than an active lesion.",
      'Other Foot Pathology': "A foot finding that doesn't clearly match the platform's more specific ulcer categories — clinician review recommended.",
    };
    
    return explanations[conditionName] || "No detailed explanation available for this condition.";
  };

  const handleConditionHover = (condition) => {
    setActiveExplanation(condition.name);
  };

  const handleConditionLeave = () => {
    if (!showExplanation) {
      setActiveExplanation(null);
    }
  };

  const toggleExplanation = (condition) => {
    if (activeExplanation === condition.name && showExplanation) {
      setShowExplanation(false);
      setActiveExplanation(null);
    } else {
      setShowExplanation(true);
      setActiveExplanation(condition.name);
    }
  };

  return (
    <div className="space-y-6">
      {/* Image Viewer with Heatmap */}
      <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
        <div className="p-5 border-b border-gray-800 flex justify-between items-center">
          <h2 className="heading-subsection">AI Analysis Results</h2>
          <div className="flex space-x-2">
            <button 
              onClick={() => setShowHeatmap(!showHeatmap)}
              className={`px-3 py-1.5 rounded text-xs ${showHeatmap 
                ? 'bg-purple-800 text-purple-100'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'} 
                transition-colors flex items-center`}
            >
              <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {showHeatmap ? 'Hide AI Overlay' : 'Show AI Overlay'}
            </button>
          </div>
        </div>
        
        <div className="bg-black flex justify-center items-center p-2 relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-transparent to-indigo-900/30"></div>
          
          {/* Base Image */}
          <div className="relative w-full max-h-[400px] flex justify-center z-10">
            <img  
              src={image}  
              alt="Medical scan"  
              className="max-h-[400px] object-contain"
              style={{
                filter: `brightness(${brightnessValue}%) contrast(${contrastValue}%)`
              }}
            />
            
            {/* Heatmap Overlay */}
            {showHeatmap && createHeatmapMask() && (
              <div 
                className="absolute inset-0 bg-cover bg-center mix-blend-screen"
                style={{ 
                  backgroundImage: `url("${createHeatmapMask()}")`,
                }}
              />
            )}
            
            {/* AI Status Indicator */}
            {showHeatmap && (
              <div className="absolute top-2 right-2 bg-black/70 text-white text-sm px-3 py-1 rounded-full flex items-center z-20">
                <span className="h-2 w-2 rounded-full bg-green-500 mr-2 pulse-animation"></span>
                <span>AI Analysis Complete</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="brightness" className="block text-sm text-gray-400 mb-1">Brightness: {brightnessValue}%</label>
              <input  
                type="range"  
                id="brightness"  
                min="50"  
                max="150"  
                value={brightnessValue} 
                onChange={handleBrightnessChange} 
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer" 
              />
            </div>
            <div>
              <label htmlFor="contrast" className="block text-sm text-gray-400 mb-1">Contrast: {contrastValue}%</label>
              <input  
                type="range"  
                id="contrast"  
                min="50"  
                max="150"  
                value={contrastValue} 
                onChange={handleContrastChange} 
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer" 
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Findings Section */}
      <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
        <div className="p-5 border-b border-gray-800">
          <h2 className="heading-subsection">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-indigo-400">AI</span>
            <span className="ml-1">Analysis Findings</span>
          </h2>
        </div>
        <div className="p-5">
          <div className="mb-6">
            <h3 className="heading-card mb-2">Overall Findings</h3>
            <p className="text-white">{findings}</p>
          </div>
          
          <div className="mb-6">
            <div className="flex justify-between mb-1">
              <h3 className="heading-card">AI Confidence</h3>
              <span className="text-sm text-gray-400">{confidence}%</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2.5">
              <div  
                className={`h-2.5 rounded-full ${ 
                  confidence > 90 ? 'bg-gradient-to-r from-green-500 to-green-600' :  
                  confidence > 70 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :  
                  'bg-gradient-to-r from-red-500 to-red-600' 
                }`} 
                style={{ width: `${confidence}%` }} 
              ></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Detailed Findings */}
      <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
        <div className="p-5 border-b border-gray-800">
          <h2 className="heading-subsection">Detailed Findings</h2>
        </div>
        <div className="p-5">
          <div className="space-y-4">
            {conditions.map((condition, index) => (
              <div key={index} className="relative">
                <div  
                  className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${ 
                    activeCondition === condition.name  
                      ? 'bg-purple-900/30 border-l-2 border-purple-500'  
                      : 'hover:bg-gray-800' 
                  }`} 
                  onClick={() => {
                    onConditionClick(condition);
                    toggleExplanation(condition);
                  }}
                  onMouseEnter={() => handleConditionHover(condition)}
                  onMouseLeave={handleConditionLeave}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-200 font-medium flex items-center">
                      <div 
                        className="h-3 w-3 rounded-full mr-2" 
                        style={{ 
                          backgroundColor: getColorForCondition(condition) 
                        }}
                      ></div>
                      {condition.name}
                      {/* Info icon */}
                      <svg className="h-4 w-4 text-gray-400 ml-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className={`text-sm px-2 py-0.5 rounded-full ${ 
                      condition.severity === 'None' ? 'bg-green-900/30 text-green-400 border border-green-600/30' :  
                      condition.severity === 'Mild' ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-600/30' :  
                      condition.severity === 'Moderate' ? 'bg-orange-900/30 text-orange-400 border border-orange-600/30' : 
                      'bg-red-900/30 text-red-400 border border-red-600/30' 
                    }`}> 
                      {condition.severity} 
                    </span>
                  </div>
                  <div className="mt-2 flex items-center">
                    <div className="w-full bg-gray-800 rounded-full h-2 mr-2">
                      <div 
                        className="h-2 rounded-full" 
                        style={{ 
                          width: `${condition.probability}%`,
                          background: `linear-gradient(to right, ${getColorForCondition(condition)}99, ${getColorForCondition(condition)})`
                        }} 
                      ></div>
                    </div>
                    <span className="text-sm text-gray-400 w-12">{condition.probability}%</span>
                  </div>
                </div>
                
                {/* Tooltip for hover */}
                {activeExplanation === condition.name && !showExplanation && (
                  <div className="absolute z-50 bg-gray-800 text-white p-3 rounded-lg shadow-lg text-sm max-w-xs left-0 mt-2">
                    <div className="absolute -top-2 left-4 transform rotate-45 w-4 h-4 bg-gray-800"></div>
                    {getConditionExplanation(condition.name)}
                  </div>
                )}
                
                {/* Full explanation when clicked */}
                {activeExplanation === condition.name && showExplanation && (
                  <div className="mt-2 p-4 bg-gray-800 rounded-lg border border-gray-700 text-white text-sm animate-fadeIn">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-purple-300 mb-2">About {condition.name}</h4>
                      <button 
                        className="text-gray-400 hover:text-gray-300"
                        onClick={() => setShowExplanation(false)}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <p>{getConditionExplanation(condition.name)}</p>
                    
                    {/* Additional information based on severity */}
                    <div className="mt-3 pt-3 border-t border-gray-700">
                      <h5 className="font-medium text-gray-300 mb-1">What this means for you:</h5>
                      <p>
                        {condition.severity === 'None' ? 
                          "This finding is normal and requires no action." : 
                          condition.severity === 'Mild' ? 
                          "This condition is mild and may require monitoring or minor intervention." :
                          condition.severity === 'Moderate' ? 
                          "This condition requires attention and may need medical intervention." :
                          "This is a serious finding that requires prompt medical attention."}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Recommendations */}
      {recommendation && (
        <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
          <div className="p-5 border-b border-gray-800">
            <h2 className="heading-subsection">Recommendations</h2>
          </div>
          <div className="p-5">
            <div className="bg-gray-800/70 p-4 rounded-lg border-l-2 border-purple-500">
              <p className="text-white">{recommendation}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedAnalysisResult;